import React from 'react';
import { Ban, Clock, RefreshCw, LogOut } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useApproval } from '../hooks/useApproval';

interface RouterGateProps {
  children: React.ReactNode;
}

export function RouterGate({ children }: RouterGateProps) {
  const { user, loading: authLoading, redirectToPortal, signOut } = useAuth();
  const { isRejected, isPending, loading: approvalLoading } = useApproval();

  // Only redirect or block users if they are logged in but have invalid verification status.
  // Unauthenticated users (user === null) are allowed to view public spaces.
  React.useEffect(() => {
    // No automatic redirect on mount for null users here;
    // AppContent will handle redirecting to portal if a protected resource is accessed.
  }, [user, authLoading, redirectToPortal]);

  // 1. Beautiful loading spinner while session is loading
  if (authLoading || approvalLoading) {
    return (
      <div className="gate-screen animate-fade-in">
        <div className="loading-spinner"></div>
        <p style={{ color: 'var(--text-secondary)' }}>Synchronizing secure sessions...</p>
      </div>
    );
  }

  // 2. Account suspended/rejected view
  if (user && isRejected) {
    return (
      <div className="gate-screen animate-fade-in">
        <div className="glass-panel gate-panel">
          <div className="gate-icon" style={{ color: 'var(--accent)' }}>
            <Ban size={56} />
          </div>
          <h2 className="gate-title">Access Suspended</h2>
          <p className="gate-desc">
            Your resident verification has been suspended or rejected. Please contact the community management.
          </p>
          <button className="btn btn-secondary" onClick={signOut}>
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </div>
    );
  }

  // 3. Awaiting approval view
  if (user && isPending) {
    const house = user.user_metadata?.house_number || 'Pending Assignment';
    return (
      <div className="gate-screen animate-fade-in">
        <div className="glass-panel gate-panel">
          <div className="gate-icon">
            <Clock size={56} />
          </div>
          <h2 className="gate-title">Awaiting Approval</h2>
          <p className="gate-desc">
            Your profile details (House {house}) are currently being verified by the resident committee. 
            You will receive standard access once approved.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button className="btn btn-primary" onClick={() => window.location.reload()}>
              <RefreshCw size={16} /> Refresh Status
            </button>
            <button className="btn btn-secondary" onClick={signOut}>
              <LogOut size={16} /> Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 4. Authorized or anonymous public view
  return <>{children}</>;
}
